import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authService } from "../../services/authService";
import { tokenManager } from "../../utils/tokenManager";

/**
 * Helper function to set cookie
 */
// const setCookie = (name, value) => {
//   document.cookie = `${name}=${value};path=/;SameSite=Lax`;
// };

/**
 * Helper function to clear cookie
 */
// const clearCookie = (name) => {
//   document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
// };

// ======================
// 🔹 SEND OTP
// ======================
export const sendOtp = createAsyncThunk(
  "auth/sendOtp",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await authService.sendOtp({ email });
      return response;
    } catch (error) {
      console.error("Send OTP failed:", error.message);
      return rejectWithValue(error.message || "Send OTP failed");
    }
  }
);

// ======================
// 🔹 VERIFY OTP
// ======================
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await authService.verifyOtp({ email, otp });
      return response;
    } catch (error) {
      console.error("Verify OTP failed:", error.message);
      return rejectWithValue(error.message || "Verify OTP failed");
    }
  }
);
// ======================
// 🔹 RESET PASSWORD
// ======================
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (
    { email, newPassword, confirmNewPassword, passwordResetToken, otp },
    { rejectWithValue }
  ) => {
    try {
      const response = await authService.resetPassword({
        email,
        newPassword,
        confirmNewPassword,
        passwordResetToken,
        otp,
      });

      return response;
    } catch (error) {
      console.error("Reset Password failed:", error.message);
      return rejectWithValue(error.message || "Reset Password failed");
    }
  }
);

/**
 * Async thunk for user login
 */
export const loginUser = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await authService.login(email, password);

      // Store token and user data using tokenManager
      tokenManager.setToken(response.token, response.user);

      // Set token in cookie with key 'tkn'
      // if (response.token) {
      //   setCookie("tkn", response.token);
      // }

      console.log("Login successful:", {
        hasToken: !!response.token,
        user: response.user?.email,
      });

      return response;
    } catch (error) {
      console.error("Login failed:", error.message);
      return rejectWithValue(error.message || "Login failed");
    }
  }
);

export const refreshUser = createAsyncThunk(
  "auth/refreshUser",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const token = tokenManager.getToken();
      const response = await authService.refreshUser(token);

      // Store token and user data using tokenManager
      tokenManager.setToken(response.token, response.user);

      // Set token in cookie with key 'tkn'
      // if (response.token) {
      //   setCookie("tkn", response.token);
      // }

      console.log("Login successful:", {
        hasToken: !!response.token,
        user: response.user?.email,
      });

      return response;
    } catch (error) {
      console.error("Login failed:", error.message);
      return rejectWithValue(error.message || "Login failed");
    }
  }
);

/**
 * Async thunk for user logout
 */
export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      tokenManager.clearAuth();
      // Clear token cookie
      // clearCookie("tkn");
      return {};
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Logout failed");
    }
  }
);

/**
 * Async thunk for validating existing token
 */
export const validateToken = createAsyncThunk(
  "auth/validateToken",
  async (_, { rejectWithValue }) => {
    if (!tokenManager.isAuthenticated()) {
      return rejectWithValue("No valid session found");
    }

    const token = tokenManager.getToken();
    const user = tokenManager.getUser();

    if (!tokenManager.isTokenValid(token)) {
      tokenManager.clearAuth();
      return rejectWithValue("Session expired");
    }

    console.log("Token validation successful for user:", user.email);

    return {
      token,
      user,
    };
  }
);

/**
 * Get initial auth state from localStorage
 */
const getInitialAuthState = () => {
  const token = tokenManager.getToken();
  const user = tokenManager.getUser();
  const isAuthenticated =
    tokenManager.isAuthenticated() && tokenManager.isTokenValid();

  console.log("Initial auth state:", {
    token: !!token,
    user: !!user,
    isAuthenticated,
  });

  return {
    user,
    token,
    isAuthenticated,
    loading: false,
    otpLoading: false,
    error: null,
  };
};

/**
 * Initial state for auth slice
 */
const initialState = getInitialAuthState();

/**
 * Auth slice with reducers for authentication state management
 */
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      tokenManager.clearAuth();
      // Clear token cookie
      // clearCookie("tkn");
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
        console.log("Login successful, token and user stored");
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      // Logout cases
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Token validation cases
      .addCase(validateToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(validateToken.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
        console.log("Token validation successful");
      })
      .addCase(validateToken.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload;
        console.log("Token validation failed:", action.payload);
      })
      // --- Send OTP ---
      .addCase(sendOtp.pending, (state) => {
        state.otpLoading = true;
        state.error = null;
      })
      .addCase(sendOtp.fulfilled, (state) => {
        state.otpLoading = false;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.error = action.payload;
      })

      // --- Verify OTP ---
      .addCase(verifyOtp.pending, (state) => {
        state.otpLoading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.otpLoading = false;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.error = action.payload;
      })
      // --- Reset Password ---
      .addCase(resetPassword.pending, (state) => {
        state.otpLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.otpLoading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.otpLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, resetAuth } = authSlice.actions;
export default authSlice.reducer;
